<template>
  <main class="main-content doc-narrow-content">
    <!-- 欢迎区域 -->
    <section class="hero">
      <h1>银企通™ API平台快速开始指南</h1>
      <p>
        通过本指南，您将了解如何快速接入银企通™
        API平台，实现银行流水数据的自动化获取
      </p>
    </section>

    <hr />

    <!-- 环境配置 -->
    <section class="section" id="environment-config">
      <div class="section-header">
        <h2>环境配置</h2>
        <p>在开始对接前，您需要在银企通™ API平台配置相应的环境参数</p>
      </div>

      <div class="flow-container">
        <div class="flow-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>登录银企通™ API平台</h3>
            <p>使用您的企业账号登录银企通™ API平台。</p>
            <div class="image-placeholder" style="background: unset">
              <span
                ><img
                  :src="loginImg"
                  alt="银企通™ API平台登录页面"
                  style="max-width: 80%; height: auto; display: block"
                />
              </span>
            </div>
          </div>
        </div>

        <div class="flow-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>进入应用管理</h3>
            <p>
              登录成功后在菜单列表中找到"应用与设置"菜单，点击进入应用管理页面。
            </p>
            <div class="image-placeholder">
              <span
                ><img
                  :src="appIdSettingImg"
                  alt="应用管理页面"
                  style="max-width: 100%; height: auto"
              /></span>
            </div>
          </div>
        </div>

        <div class="flow-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <h3>调试一个API请求</h3>
            <p>
              接下来，以“获取签约链接”API接口为例，看看如果发送一个请求并获得正确的返回。<br />
              跳转到<router-link
                class="api-link"
                to="/sign-url"
                style="
                  color: var(--primary);
                  font-weight: bold;
                  font-style: italic;
                "
                >获取签约链接</router-link
              >接口文档，然后按照图示点击“获取签约链接”按钮。（注意：如果没有登录需要点击右上角的登录按钮，登录成功后再回到这个页面）
            </p>
            <div class="image-placeholder">
              <span
                ><img
                  :src="getSignUrlRequestImg"
                  alt="获取签约链接接口文档"
                  style="max-width: 100%; height: auto"
              /></span>
            </div>
            <p>
              点击“获取签约链接”按钮后，您将在下方看到返回的JSON数据，这表示您已成功调用了“获取签约链接”API接口。
            </p>
            <div class="image-placeholder">
              <span
                ><img
                  :src="getSignUrlResponseImg"
                  alt="获取签约链接接口返回"
                  style="max-width: 100%; height: auto"
              /></span>
            </div>

            <div class="api-details">
              <pre>
// Sandbox环境（测试使用）
Server: <span id="sandbox-url">{{ systemStore.sandboxUrl }}</span>
              </pre>
            </div>

            <div class="api-details">
              <pre>
// Live环境（生产使用）
Server: <span id="production-url">{{ systemStore.productionUrl }}</span>
              </pre>
            </div>

            <div class="note">
              <strong>测试建议：</strong
              >在开发和测试阶段，请使用Sandbox环境参数，
              避免对生产环境造成影响。
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 安全认证 -->
    <!--    <section class="section" id="security-auth">
      <div class="section-header">
        <h2>安全认证</h2>
        <p>调用银企通™ API平台API时，需要在HTTP Header中携带安全认证信息</p>
      </div>
      
      <div class="api-details">
        <pre>// securityCode生成算法
const appSecret = 'KHPuj0JMS6nS1ACzc';  // 您的AppSecret
const today = new Date();
const dateStr = `${today.getFullYear()}${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}`;

// 计算securityCode（小写MD5）
const securityCode = md5(`${appSecret}#${dateStr}`).toLowerCase();

// 在API请求头中携带
Headers:
    x-xencio-client-id: [您的AppID]
    securityCode: [计算得到的securityCode]</pre>
      </div>
      
      <div class="note">
        <strong>注意：</strong>securityCode需要每日更新，因为它基于当前日期计算。
        请确保您的服务器时间与北京时间保持一致。
      </div>
    </section>  -->

    <!-- 下一步 -->
    <section class="section" id="next-steps">
      <div class="section-header">
        <h2>下一步</h2>
        <p>完成快速开始指南后，您可以继续探索更多功能</p>
      </div>

      <div class="card-grid">
        <div class="card">
          <div class="card-icon">
            <i class="fas fa-book"></i>
          </div>
          <h3>查看API文档</h3>
          <p>详细了解每个API接口的参数、返回值和调用示例。</p>
          <a href="introduction" class="card-link"
            >查看文档 <i class="fas fa-arrow-right"></i
          ></a>
        </div>

        <!-- <div class="card">
          <div class="card-icon">
            <i class="fas fa-headset"></i>
          </div>
          <h3>联系客服</h3>
          <p>获取技术支持或申请切换到生产环境。</p>
          <a href="#" class="card-link">联系客服 <i class="fas fa-arrow-right"></i></a>
        </div> -->
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, onUpdated } from "vue";
import { useCodeHighlight } from "@/composables/useCodeHighlight";
import { useSystemStore } from "@/stores/system";
import appIdSettingImg from "@/assets/appIdSetting.png";
import getSignUrlRequestImg from "@/assets/get_sign_url_request.png";
import getSignUrlResponseImg from "@/assets/get_sign_url_response.png";
import loginImg from "@/assets/login.png";

// 定义状态表项的接口
export interface StatusItem {
  code: string;
  description: string;
  nextStep: string;
}

// 定义流程步骤的接口
export interface Step {
  number: string;
  title: string;
  description: string;
  hasImage?: boolean;
  icon?: string;
  imageText?: string;
  hasCode?: boolean;
  code?: string;
  hasNote?: boolean;
  note?: string;
  hasTable?: boolean;
  statusTable?: StatusItem[];
  subtitle?: string;
}

// 激活的流程类型
const activeFlow = ref("normal");

// 切换流程
const switchFlow = (flow: string) => {
  activeFlow.value = flow;
};

// 使用代码高亮功能
useCodeHighlight(".api-details pre");

// 系统配置
const systemStore = useSystemStore();

// 对接流程步骤数据
const dockingSteps = ref<Step[]>([
  {
    number: "1",
    title: "注册银企通™ API平台",
    description: "访问银企通™ API平台官网，完成企业注册和实名认证流程。",
    hasImage: false,
    hasCode: false,
    hasNote: false,
    hasTable: false,
  },
  {
    number: "2",
    title: "配置应用信息",
    description:
      '登录银企通™ API平台，进入"应用与设置"管理页面，设置Sandbox和Live环境的AppID和Secret。',
    hasImage: true,
    icon: "fas fa-cogs",
    imageText: "应用与设置页面截图（此处放置截图）",
    hasCode: false,
    hasNote: false,
    hasTable: false,
  },
  {
    number: "3",
    title: "获取签约链接",
    description: '调用"获取签约链接"接口，获取云直连签约网站的URL。',
    hasImage: false,
    hasCode: true,
    code: `POST /api/sign/url
Headers:
    x-xencio-client-id: [您的AppID]
    securityCode: [根据AppSecret计算的校验码]

Body:
    bankCode: "ICBC"  // 银行编码
    callbackUrl: "https://yourdomain.com/callback"  // 回调地址`,
    hasNote: false,
    hasTable: false,
  },
  {
    number: "4",
    title: "完成云直连签约",
    description: "打开签约链接，填写银行账号和信息，提交后等待银行审批。",
    hasImage: true,
    icon: "fas fa-file-signature",
    imageText: "云直连签约页面截图（此处放置截图）",
    hasCode: false,
    hasNote: true,
    note: "不同银行的签约流程可能略有不同，部分银行可能需要登录企业网银进行确认。请参考银企通™ API平台提供的各银行签约步骤说明。",
    hasTable: false,
  },
  {
    number: "5",
    title: "查询签约状态",
    description: '调用"获取签约状态查询/数据同步状态"接口，监控签约流程状态。',
    hasImage: false,
    hasCode: true,
    code: `POST /api/sign/status
Headers:
    x-xencio-client-id: [您的AppID]
    securityCode: [根据AppSecret计算的校验码]

Body:
    signId: "SIGN202305120001"  // 签约ID`,
    hasNote: false,
    hasTable: false,
  },
  {
    number: "6",
    title: "等待数据同步",
    description:
      '当签约状态变为"签约成功"后，银企通™ API平台会自动同步该账号最近30天的流水数据。',
    subtitle: "状态说明",
    hasImage: false,
    hasCode: false,
    hasNote: false,
    hasTable: true,
    statusTable: [
      { code: "100", description: "签约中", nextStep: "等待银行审批" },
      { code: "200", description: "签约成功", nextStep: "等待数据同步" },
      { code: "300", description: "数据同步中", nextStep: "等待同步完成" },
      { code: "400", description: "数据同步完成", nextStep: "可进行流水查询" },
      {
        code: "500",
        description: "签约失败",
        nextStep: "检查失败原因重新签约",
      },
    ],
  },
  {
    number: "7",
    title: "查询银行流水",
    description:
      '当状态变为"数据同步完成"后，即可调用"流水查询接口"获取银行流水数据。',
    hasImage: false,
    hasCode: true,
    code: `POST /bs/list
Headers:
    x-xencio-client-id: [您的AppID]
    securityCode: [根据AppSecret计算的校验码]

Body:
    bankAccNo: "12345678901234567890"  // 银行账号
    startDate: "20230501"  // 开始日期
    endDate: "20230531"    // 结束日期
    page: 1                // 页码
    pageSize: 20           // 每页大小`,
    hasNote: false,
    hasTable: false,
  },
]);
</script>

<style scoped>
.hero {
  background: linear-gradient(120deg, #7486f1, #955bea);
  border-radius: 12px;
  padding: 2.5rem;
  color: white;
  margin-bottom: 2rem;
}

.hero h1 {
  font-size: 2.25rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.hero p {
  font-size: 1.1rem;
  max-width: 700px;
  margin-bottom: 1.5rem;
  opacity: 0.9;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.card {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background-color: rgba(58, 109, 248, 0.1);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.card h3 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.card p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.card-link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.card-link i {
  margin-left: 0.5rem;
  transition: transform 0.2s;
}

.card-link:hover i {
  transform: translateX(3px);
}

.section {
  margin-bottom: 3rem;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h2 {
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.section-header p {
  color: var(--text-secondary);
}

.api-reference {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.api-item {
  border-bottom: 1px solid var(--border);
  padding: 1.5rem;
}

.api-item:last-child {
  border-bottom: none;
}

.api-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.api-method {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.75rem;
}

.method-get {
  background-color: rgba(71, 184, 129, 0.1);
  color: #47b881;
}

.method-post {
  background-color: rgba(58, 109, 248, 0.1);
  color: var(--primary);
}

.api-url {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.api-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.api-details {
  color: var(--code-color);
  border-radius: 8px;

  font-size: 0.85rem;
  overflow-x: auto;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.api-details pre {
  margin: 0;
  white-space: pre-wrap;
  background-color: #000 !important;
  color: #fff !important;
  padding: 1rem;
  border-radius: 4px;
}

.try-section {
  background-color: var(--secondary);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.try-section h4 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.9rem;
}

.response-area {
  margin-top: 1.5rem;
  background-color: var(--code-bg);
  border-radius: 8px;
  padding: 1rem;
  color: var(--code-color);
  font-size: 0.85rem;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.response-area pre {
  margin: 0;
  white-space: pre-wrap;
}

.param-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.param-table th,
.param-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.param-table th {
  background-color: var(--secondary);
  font-weight: 600;
}

/* 快速开始特定样式 */
.flow-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 2rem 0;
}

.flow-step {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.step-number {
  margin-top: 0.5rem;
  background: var(--primary);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content h3 {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.step-content h4 {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.step-content p {
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

.image-placeholder {
  background: var(--secondary);
  /*border: 2px dashed var(--border);*/
  border-radius: 8px;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  margin-right: 20px;
  width: fit-content;
  color: var(--text-secondary);
  /*padding: 1rem;*/
}

.image-placeholder i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.note {
  background: rgba(255, 193, 7, 0.1);
  border-left: 4px solid #ffc107;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
}

.status-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.status-table th,
.status-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.status-table th {
  background-color: var(--secondary);
  font-weight: 600;
}

.status-table tr:last-child td {
  border-bottom: none;
}

/* 企业操作流程说明样式 */
.section h1 {
  font-size: 1.8rem;
  color: var(--text-primary);
  margin: 1.5rem 0 1rem;
  font-weight: 600;
}

.section h2 {
  font-size: 1.4rem;
  color: var(--text-primary);
  margin: 1.2rem 0 0.8rem;
  font-weight: 500;
}

.section h3 {
  font-size: 1.1rem;
  color: var(--text-primary);
  margin: 1rem 0 0.5rem;
  font-weight: 500;
}

.section h4 {
  font-size: 1rem;
  color: var(--text-primary);
  margin: 0.8rem 0 0.4rem;
  font-weight: 500;
}

.section ol,
.section ul {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

.section li {
  margin-bottom: 0.8rem;
}

.section p {
  color: var(--text-secondary);
  line-height: 1.6;
}

.enterprise-content {
  transition: opacity 0.3s ease;
}

.btn-toggle {
  margin: 1rem 0;
}

.btn-toggle .btn {
  margin-right: 0.5rem;
}

.btn-toggle .btn:last-child {
  margin-right: 0;
}

.btn-toggle .btn.active {
  background-color: var(--primary);
  color: white;
  border-color: var(--primary);
}

/* 图片立体阴影效果 */
.image-placeholder img {
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background-color: #fff; /* 提升透明PNG的立体感 */
}

.image-placeholder img:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.16), 0 6px 14px rgba(0, 0, 0, 0.1);
}
</style>
